import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send, Calendar, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import GdprNotice from '@/components/GdprNotice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactSection = () => {
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [visitOpen, setVisitOpen] = useState(false);
  const [brochureOpen, setBrochureOpen] = useState(false);

  // Enquiry form state
  const [enquiryForm, setEnquiryForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [enquirySubmitting, setEnquirySubmitting] = useState(false);

  // Visit booking form state
  const [visitForm, setVisitForm] = useState({
    name: '', email: '', phone: '', preferred_date: '', preferred_time: '', care_type: '', additional_info: ''
  });
  const [visitSubmitting, setVisitSubmitting] = useState(false);

  // Brochure form state
  const [brochureForm, setBrochureForm] = useState({ name: '', email: '', phone: '', address: '' });
  const [brochureSubmitting, setBrochureSubmitting] = useState(false);

  // Handle enquiry submission
  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    setEnquirySubmitting(true);
    try {
      await axios.post(`${API}/enquiry`, { ...enquiryForm, enquiry_type: 'general' });
      toast.success('Enquiry sent successfully! We will be in touch soon.');
      setEnquiryForm({ name: '', email: '', phone: '', message: '' });
      setEnquiryOpen(false);
    } catch (error) {
      toast.error('Failed to send enquiry. Please try again.');
    } finally {
      setEnquirySubmitting(false);
    }
  };

  // Handle visit booking submission
  const handleVisitSubmit = async (e) => {
    e.preventDefault();
    setVisitSubmitting(true);
    try {
      await axios.post(`${API}/visit-booking`, visitForm);
      toast.success('Visit booking request sent! We will confirm your appointment soon.');
      setVisitForm({ name: '', email: '', phone: '', preferred_date: '', preferred_time: '', care_type: '', additional_info: '' });
      setVisitOpen(false);
    } catch (error) {
      toast.error('Failed to submit booking request. Please try again.');
    } finally {
      setVisitSubmitting(false);
    }
  };

  // Handle brochure request submission
  const handleBrochureSubmit = async (e) => {
    e.preventDefault();
    setBrochureSubmitting(true);
    try {
      await axios.post(`${API}/brochure-request`, brochureForm);
      toast.success('Brochure request sent! You will receive it shortly.');
      setBrochureForm({ name: '', email: '', phone: '', address: '' });
      setBrochureOpen(false);
    } catch (error) {
      toast.error('Failed to request brochure. Please try again.');
    } finally {
      setBrochureSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-32 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#c9a227]/10 rounded-full text-[#c9a227] text-sm font-medium mb-6">
            Contact Us
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e3a5f] mb-6">
            Arrange a Visit Today
          </h2>
          <p className="text-slate-600 text-lg">
            We welcome enquiries from individuals, families and professionals.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info & Map */}
          <div>
            <div className="bg-[#1e3a5f] rounded-2xl p-8 text-white mb-8">
              <h3 className="text-2xl font-bold mb-8">
                Get in Touch
              </h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#c9a227]" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Address</h4>
                    <p className="text-white/80">17 Queen's Road,<br />Hoylake, Wirral</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#c9a227]" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Phone</h4>
                    <a href="tel:01516324504" className="text-white/80 hover:text-[#c9a227] transition-colors">
                      0151 632 4504
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#c9a227]" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Email</h4>
                    <a href="mailto:manager@abneylownursing.co.uk" className="text-white/80 hover:text-[#c9a227] transition-colors">
                      manager@abneylownursing.co.uk
                    </a>
                  </div>
                </div>
              </div>
              <div className="mt-8 pt-8 border-t border-white/20">
                <h4 className="font-semibold mb-3">Opening Hours</h4>
                <p className="text-white/80">Visits welcome 7 days a week<br />Please contact us to arrange</p>
              </div>
            </div>
            
            {/* Google Maps */}
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2373.5!2d-3.1825!3d53.3892!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x487b22c31c88c777%3A0x6b09c15f0c0e3b93!2sQueen&#39;s%20Rd%2C%20Hoylake%2C%20Wirral!5e0!3m2!1sen!2suk!4v1699999999999!5m2!1sen!2suk"
                width="100%"
                height="250"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Abney Low Nursing Home Location"
                data-testid="google-map"
              />
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="space-y-6">
            {/* Book a Visit */}
            <Dialog open={visitOpen} onOpenChange={setVisitOpen}>
              <DialogTrigger asChild>
                <div className="bg-slate-50 rounded-2xl p-8 cursor-pointer hover:shadow-lg transition-all border border-slate-100 hover:border-[#c9a227]/30" data-testid="book-visit-card">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-xl bg-[#c9a227] flex items-center justify-center">
                      <Calendar className="w-7 h-7 text-[#1e3a5f]" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#1e3a5f]">Book a Visit</h3>
                      <p className="text-slate-600">Schedule a tour of our home</p>
                    </div>
                  </div>
                  <Button className="w-full bg-[#c9a227] text-[#1e3a5f] hover:bg-[#d4b03a] rounded-full">
                    Book Now
                  </Button>
                </div>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto bg-white border border-slate-200 shadow-xl">
                <DialogHeader>
                  <DialogTitle className="text-2xl text-[#1e3a5f]">
                    Book a Visit
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleVisitSubmit} className="space-y-4 mt-4" data-testid="visit-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Full Name *</Label>
                      <Input value={visitForm.name} onChange={(e) => setVisitForm({ ...visitForm, name: e.target.value })} required className="mt-2 rounded-xl" data-testid="visit-name-input" />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input type="email" value={visitForm.email} onChange={(e) => setVisitForm({ ...visitForm, email: e.target.value })} required className="mt-2 rounded-xl" data-testid="visit-email-input" />
                    </div>
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input value={visitForm.phone} onChange={(e) => setVisitForm({ ...visitForm, phone: e.target.value })} required className="mt-2 rounded-xl" data-testid="visit-phone-input" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Preferred Date *</Label>
                      <Input type="date" value={visitForm.preferred_date} onChange={(e) => setVisitForm({ ...visitForm, preferred_date: e.target.value })} required className="mt-2 rounded-xl" data-testid="visit-date-input" />
                    </div>
                    <div>
                      <Label>Preferred Time *</Label>
                      <Select value={visitForm.preferred_time} onValueChange={(value) => setVisitForm({ ...visitForm, preferred_time: value })}>
                        <SelectTrigger className="mt-2 rounded-xl" data-testid="visit-time-select"><SelectValue placeholder="Select time" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="morning">Morning (9am - 12pm)</SelectItem>
                          <SelectItem value="afternoon">Afternoon (12pm - 5pm)</SelectItem>
                          <SelectItem value="evening">Evening (5pm - 7pm)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Type of Care Required *</Label>
                    <Select value={visitForm.care_type} onValueChange={(value) => setVisitForm({ ...visitForm, care_type: value })}>
                      <SelectTrigger className="mt-2 rounded-xl" data-testid="visit-care-select"><SelectValue placeholder="Select care type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nursing">Nursing Care</SelectItem>
                        <SelectItem value="residential">Residential Care</SelectItem>
                        <SelectItem value="dementia_nursing">Dementia Nursing</SelectItem>
                        <SelectItem value="dementia_residential">Dementia Residential</SelectItem>
                        <SelectItem value="mental_health_nursing">Mental Health Nursing</SelectItem>
                        <SelectItem value="bariatric_nursing">Bariatric Nursing</SelectItem>
                        <SelectItem value="unsure">Not sure yet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Additional Information</Label>
                    <Textarea value={visitForm.additional_info} onChange={(e) => setVisitForm({ ...visitForm, additional_info: e.target.value })} className="mt-2 rounded-xl" placeholder="Any specific requirements?" data-testid="visit-info-textarea" />
                  </div>
                  <GdprNotice testId="visit-gdpr-notice" />
                  <Button type="submit" disabled={visitSubmitting} className="w-full bg-[#c9a227] text-[#1e3a5f] hover:bg-[#d4b03a] rounded-full py-3" data-testid="visit-submit-btn">
                    {visitSubmitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Submitting...</> : <><Calendar className="w-5 h-5 mr-2" />Book Visit</>}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Request Brochure */}
            <Dialog open={brochureOpen} onOpenChange={setBrochureOpen}>
              <DialogTrigger asChild>
                <div className="bg-slate-50 rounded-2xl p-8 cursor-pointer hover:shadow-lg transition-all border border-slate-100 hover:border-[#c9a227]/30" data-testid="brochure-card">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-xl bg-[#1e3a5f] flex items-center justify-center">
                      <FileText className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#1e3a5f]">Request a Brochure</h3>
                      <p className="text-slate-600">Get our information pack</p>
                    </div>
                  </div>
                  <Button className="w-full bg-[#1e3a5f] text-white hover:bg-[#162c4a] rounded-full">
                    Request Now
                  </Button>
                </div>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg bg-white border border-slate-200 shadow-xl">
                <DialogHeader>
                  <DialogTitle className="text-2xl text-[#1e3a5f]">
                    Request a Brochure
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleBrochureSubmit} className="space-y-4 mt-4" data-testid="brochure-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Full Name *</Label>
                      <Input value={brochureForm.name} onChange={(e) => setBrochureForm({ ...brochureForm, name: e.target.value })} required className="mt-2 rounded-xl" data-testid="brochure-name-input" />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input type="email" value={brochureForm.email} onChange={(e) => setBrochureForm({ ...brochureForm, email: e.target.value })} required className="mt-2 rounded-xl" data-testid="brochure-email-input" />
                    </div>
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input value={brochureForm.phone} onChange={(e) => setBrochureForm({ ...brochureForm, phone: e.target.value })} className="mt-2 rounded-xl" data-testid="brochure-phone-input" />
                  </div>
                  <div>
                    <Label>Postal Address (for printed brochure)</Label>
                    <Textarea value={brochureForm.address} onChange={(e) => setBrochureForm({ ...brochureForm, address: e.target.value })} className="mt-2 rounded-xl" placeholder="Enter your address" data-testid="brochure-address-textarea" />
                  </div>
                  <GdprNotice testId="brochure-gdpr-notice" />
                  <Button type="submit" disabled={brochureSubmitting} className="w-full bg-[#c9a227] text-[#1e3a5f] hover:bg-[#d4b03a] rounded-full py-3" data-testid="brochure-submit-btn">
                    {brochureSubmitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Requesting...</> : <><FileText className="w-5 h-5 mr-2" />Request Brochure</>}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Send Enquiry */}
            <Dialog open={enquiryOpen} onOpenChange={setEnquiryOpen}>
              <DialogTrigger asChild>
                <div className="bg-slate-50 rounded-2xl p-8 cursor-pointer hover:shadow-lg transition-all border border-slate-100 hover:border-[#c9a227]/30" data-testid="enquiry-card">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-xl bg-emerald-600 flex items-center justify-center">
                      <Send className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#1e3a5f]">Send an Enquiry</h3>
                      <p className="text-slate-600">Ask us anything</p>
                    </div>
                  </div>
                  <Button className="w-full bg-emerald-600 text-white hover:bg-emerald-700 rounded-full">
                    Send Enquiry
                  </Button>
                </div>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg bg-white border border-slate-200 shadow-xl">
                <DialogHeader>
                  <DialogTitle className="text-2xl text-[#1e3a5f]">
                    Send an Enquiry
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleEnquirySubmit} className="space-y-4 mt-4" data-testid="enquiry-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Full Name *</Label>
                      <Input value={enquiryForm.name} onChange={(e) => setEnquiryForm({ ...enquiryForm, name: e.target.value })} required className="mt-2 rounded-xl" data-testid="enquiry-name-input" />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input type="email" value={enquiryForm.email} onChange={(e) => setEnquiryForm({ ...enquiryForm, email: e.target.value })} required className="mt-2 rounded-xl" data-testid="enquiry-email-input" />
                    </div>
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input value={enquiryForm.phone} onChange={(e) => setEnquiryForm({ ...enquiryForm, phone: e.target.value })} required className="mt-2 rounded-xl" data-testid="enquiry-phone-input" />
                  </div>
                  <div>
                    <Label>Message *</Label>
                    <Textarea value={enquiryForm.message} onChange={(e) => setEnquiryForm({ ...enquiryForm, message: e.target.value })} required className="mt-2 rounded-xl min-h-[120px]" placeholder="How can we help you?" data-testid="enquiry-message-textarea" />
                  </div>
                  <GdprNotice testId="enquiry-gdpr-notice" />
                  <Button type="submit" disabled={enquirySubmitting} className="w-full bg-[#c9a227] text-[#1e3a5f] hover:bg-[#d4b03a] rounded-full py-3" data-testid="enquiry-submit-btn">
                    {enquirySubmitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Sending...</> : <><Send className="w-5 h-5 mr-2" />Send Enquiry</>}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
